import { h, VNode } from 'preact';
import {
  useState,
  useEffect,
  useCallback,
} from 'preact/hooks';
import './style.css';
import {
  ClubEvent,
  ClubEventItem,
  UserInput,
} from './types';
import { useGetClubEventItems } from './hooks/useGetClubEventItems';
import { useRequestPublicGuestlist } from './hooks/useRequestPublicGuestlist';
import { EventSelector } from './components/EventSelector';
import { GuestlistForm } from './components/GuestlistForm';
import { UserDetailsForm } from './components/UserDetailsForm';
import { SuccessView } from './components/SuccessView';
import { ErrorView } from './components/ErrorView';

interface Props {
  clubId: number;
}

const emailRegExp = new RegExp(
  /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-ZåäöÅÄÖ]{2,}))$/,
);
const nameRegExp = /^[\p{L}\s']+$/u;

export default function App(props: Props): VNode {
  const [selectedEvent, setSelectedEvent] =
    useState<ClubEvent | null>(null);
  const [selectedEventItem, setSelectedEventItem] =
    useState<ClubEventItem | null>(null);
  const [currentStep, setCurrentStep] = useState(1);

  const [userInput, setUserInput] = useState<UserInput>({
    name: '',
    age: 0,
    email: '',
    plusOnes: 0,
  });

  const [formErrors, setFormErrors] = useState({
    name: false,
    age: false,
    email: false,
  });

  const {
    clubEvents,
    isLoading,
    errorMessage: fetchError,
  } = useGetClubEventItems({
    clubId: props.clubId,
  });

  const [
    requestGuestlist,
    {
      isLoading: isRequestingGuestlist,
      errorMessage: requestError,
    },
  ] = useRequestPublicGuestlist({ clubId: props.clubId });

  // If event not selected, set first event as default
  useEffect(() => {
    if (clubEvents.length > 0) {
      const selectedEventItem = clubEvents[0].eventItems[0];

      setSelectedEvent(prev =>
        !prev ? clubEvents[0] : prev,
      );

      setSelectedEventItem(selectedEventItem);
    }
  }, [clubEvents]);

  const onChangeEvent = useCallback(
    (eventId: number) => {
      const selectedEvent = clubEvents.find(
        e => e.id == eventId,
      );

      if (selectedEvent) {
        setSelectedEvent(selectedEvent);
        setSelectedEventItem(selectedEvent.eventItems[0]);
      }
    },
    [clubEvents],
  );

  const onChangeEventItem = useCallback(
    (eventItemId: number) => {
      const selectedEventItem =
        selectedEvent?.eventItems.find(
          ei => ei.id == eventItemId,
        );
      setSelectedEventItem(selectedEventItem);
    },
    [selectedEvent],
  );

  const validateStep = (step: number): boolean => {
    const newErrors = { ...formErrors };
    let isValid = true;

    if (step === 2) {
      newErrors.name =
        !userInput.name || !nameRegExp.test(userInput.name);
      isValid = !newErrors.name;
    } else if (step === 3) {
      newErrors.email =
        !userInput.email ||
        !emailRegExp.test(userInput.email);
      newErrors.age =
        !userInput.age ||
        userInput.age < selectedEvent.age ||
        userInput.age > 99;
      isValid = !newErrors.email && !newErrors.age;
    }

    setFormErrors(newErrors);
    return isValid;
  };

  const moveToStep = (step: number) => {
    if (validateStep(step)) {
      if (step === 3) {
        onSubmit();
      } else {
        setCurrentStep(step);
      }
    }
  };

  const onSubmit = async () => {
    if (!selectedEvent || !selectedEventItem) return;

    const requestBody = {
      eventId: selectedEvent.id,
      eventItemId: selectedEventItem.id,
      name: userInput.name,
      email: userInput.email,
      plusOnes: userInput.plusOnes ?? 0,
      age: userInput.age,
    };

    await requestGuestlist(requestBody);
    setCurrentStep(3);
  };

  if (isLoading) {
    return (
      <div className="body">
        <div className="container">
          <div className="subhead">Loading events...</div>
        </div>
      </div>
    );
  }

  if (fetchError || requestError) {
    return (
      <div className="body">
        <ErrorView
          errorMessage={fetchError || requestError}
        />
      </div>
    );
  }

  return (
    <div className="body">
      <div className="container">
        {currentStep === 1 ? (
          <div className="contentContainer">
            <EventSelector
              clubEvents={clubEvents}
              selectedEvent={selectedEvent}
              onChangeEvent={onChangeEvent}
              onChangeEventItem={onChangeEventItem}
            />

            <GuestlistForm
              userInput={userInput}
              selectedEventItem={selectedEventItem}
              showNameError={formErrors.name}
              onUpdateUserInput={setUserInput}
            />

            <button
              id="button"
              disabled={isLoading || !clubEvents.length}
              aria-label="Proceed to next step"
              onClick={() => {
                clubEvents.length > 0 && moveToStep(2);
              }}>
              NEXT STEP
            </button>
          </div>
        ) : null}

        {currentStep === 2 ? (
          <div className="contentContainer">
            <UserDetailsForm
              userInput={userInput}
              showEmailError={formErrors.email}
              showAgeError={formErrors.age}
              onUpdateUserInput={setUserInput}
            />

            <button
              id="button"
              onClick={() => {
                moveToStep(3);
              }}>
              {isRequestingGuestlist
                ? 'Submitting...'.toUpperCase()
                : 'Request guestlist'.toUpperCase()}
            </button>
            <div>
              <div
                className="caption"
                id="noteOne">
                By tapping Request guestlist, your form will
                be sent to Nightli, and data will be saved
                and shared with a third-party business.
              </div>
              <div
                className="caption"
                id="noteOne">
                By tapping Request guestlist you Agree to
                our
                <a
                  href="https://app.nightli.se/all-terms-conditions"
                  id="noteTwo">
                  Terms
                </a>
              </div>
            </div>
          </div>
        ) : null}

        {currentStep === 3 ? <SuccessView /> : null}

        <img
          src="https://uploads-ssl.webflow.com/604e5bf6813f947d0e60c30d/621b704ca8b443366a377c7d_nightli%20vit-09.svg"
          width="150"
          height="100"
          loading="lazy"
          alt="nightli my social nightlife app"
        />
      </div>
    </div>
  );
}
